import { getDemoDatabase } from './index';
import { normalizeEmail } from '../../utils/normalizeEmail';
import { roundMoney } from '../../utils/libs';
import type { ProductVariant } from '../../redux/productSlice';
import {
  getProductKind,
  isServiceLine,
  type ProductKind,
} from '../../utils/productKind';
import {
  getDemoRow,
  insertDemoRow,
  parseJsonArray,
  parseJsonObject,
  readBool,
  readNumber,
  readText,
  toSqlBool,
  updateDemoRow,
} from './sql';
import type { DemoRow } from './tables';

export interface DemoCartCheckoutItem {
  product_id: string;
  variant_id?: string;
  quantity: number;
  mass: number;
  unit_price: number;
  price_type: 'item' | 'unit';
  /** Left out for products. */
  kind?: ProductKind;
  name: string;
  unit_of_mass: string;
  category: string;
  image: string;
}

export interface DemoProductInformationSnapshot {
  _id: string;
  bp_owner_id: string;
  bp_product_location_id: string;
  bp_product_barcode: string;
  bp_product_name: string;
  bp_product_category: string;
  bp_product_sub_category: string;
  bp_product_price_type: 'item' | 'unit';
  bp_product_kind: ProductKind;
  bp_product_unit_of_mass: string;
  bp_product_mass: string;
  bp_product_buying_price: number;
  bp_product_selling_price: number;
  bp_product_quantity: number;
  bp_product_size: string;
  bp_minimum_stock_level: number;
  bp_product_model: string;
  bp_product_image: string;
  bp_product_deleted: boolean;
  bp_product_is_favourite: boolean;
  bp_product_created_at: number;
  bp_product_updated_at: number;
  bp_variant_id?: string;
  bp_variant_attributes?: Record<string, string>;
  bp_product_has_variants?: boolean;
  bp_product_parent_name?: string;
}

export interface DemoSalesProductInformation {
  bp_sales_id: string;
  bp_employee_id: string;
  bp_owner_id: string;
  bp_order_id: string;
  bp_product_id: string;
  bp_order_quantity: number;
  bp_order_price: number;
  bp_order_paid_quantity: number;
  bp_order_completed: boolean;
  bp_order_partial_paid: boolean;
  bp_order_created_at: number;
  bp_order_updated_at: number;
  bp_order_deleted_at: number;
  bp_order_mass: number;
  bp_order_paid_mass: number;
  bp_sales_location_id: string;
  bp_selling_price_changed: boolean;
  bp_order_price_type: 'item' | 'unit';
  bp_product_information: DemoProductInformationSnapshot;
}

export interface DemoPaymentInformationObject {
  _id: string;
  bp_payment_status: boolean;
  bp_payment_amount: number;
  bp_payment_amount_received: number;
  bp_payment_already_paid_amount: number;
  bp_payment_amount_actually_received: number;
  bp_payment_amount_due: number;
  bp_payment_amount_refunded: number;
  bp_payment_refunds: DemoPaymentRefundEntry[];
  bp_order_information_id: string;
  bp_payment_created_at: number;
  bp_payment_updated_at: number;
  bp_payment_deleted_at: number;
  bp_payments: DemoPaymentEntry[];
  bp_payment_location_id: string;
  bp_payment_method: string;
}

export interface DemoPaymentEntry {
  method: string;
  amount: number;
  timestamp: number;
}

export interface DemoPaymentRefundEntry {
  payment_id?: string;
  reason?: string;
  amount: number;
  timestamp: number;
}

export interface DemoHydratedOrder {
  bp_order_id: string;
  bp_order_master_owner_id: string;
  bp_order_master_employee_id: string;
  bp_order_customer_information: string;
  bp_order_customer_email_information: string;
  bp_order_customer_phone_number_information: string;
  bp_order_customer_information_updated_at: number;
  bp_order_master_order_status: 'COMPLETED' | 'PENDING' | 'HOLD' | 'PARTIAL';
  bp_order_master_payment_information: DemoPaymentInformationObject;
  bp_order_master_receipt_reference_id: string;
  bp_order_master_receipt_ref_id_updated_at: number;
  bp_sales_products_information: DemoSalesProductInformation[];
  bp_order_master_created_at: number;
  bp_order_master_updated_at: number;
  bp_order_master_deleted_at: number;
  bp_order_master_location_id: string;
  bp_order_master_order_total: number;
  bp_total_sales_products: number;
}

export interface DemoCheckoutNavigationPayload {
  order: DemoSalesProductInformation[];
  orderID: string;
  Payment_Information: DemoPaymentInformationObject;
  ReceiptID: string;
}

export interface CreateDemoOrderParams {
  cartItems: DemoCartCheckoutItem[];
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  businessName?: string;
  ownerId?: string;
  employeeId?: string;
  locationId?: string;
  paymentMethod?: 'Cash' | 'Mpesa';
}

const DEFAULT_OWNER_ID = 'demo_owner';
const DEFAULT_LOCATION_ID = 'demo_location';
const DEFAULT_EMPLOYEE_ID = '';
const DEFAULT_BUSINESS_NAME = 'demo location';

function normalizePriceType(value: string): 'item' | 'unit' {
  return value === 'unit' ? 'unit' : 'item';
}

function toLineTotal(item: DemoCartCheckoutItem): number {
  const qty = item.price_type === 'unit' ? item.mass : item.quantity;
  return roundMoney(item.unit_price * Math.max(0, qty));
}

async function generateUniqueReceiptReferenceId(
  businessName: string,
): Promise<string> {
  const db = await getDemoDatabase();
  const businessPrefix = businessName
    ? businessName.substring(0, 2).toUpperCase()
    : 'BP';
  const maxAttempts = 10;
  let attempt = 0;

  while (attempt < maxAttempts) {
    try {
      const randomPart = Math.floor(100000000 + Math.random() * 900000000);
      const receiptReferenceId = `${businessPrefix}${randomPart}`;

      const { count: existingCount } = await db.get<{ count: number }>(
        `SELECT COUNT(*) AS count FROM demo_order_master_information
         WHERE bp_order_master_receipt_reference_id = ?`,
        [receiptReferenceId],
      );

      if (existingCount === 0) {
        return receiptReferenceId;
      }
    } catch (error) {
      console.error('Error checking receipt reference ID:', error);
    }
    attempt += 1;
  }

  throw new Error(
    'Failed to generate a unique receipt reference ID after multiple attempts',
  );
}

function buildFallbackProductSnapshot(
  item: DemoCartCheckoutItem,
  ownerId: string,
  locationId: string,
  now: number,
): DemoProductInformationSnapshot {
  return {
    _id: item.product_id,
    bp_owner_id: ownerId,
    bp_product_location_id: locationId,
    bp_product_barcode: '',
    bp_product_name: item.name,
    bp_product_category: item.category,
    bp_product_sub_category: item.category,
    bp_product_price_type: item.price_type,
    bp_product_kind: isServiceLine(item) ? 'service' : 'product',
    bp_product_unit_of_mass: item.unit_of_mass,
    bp_product_mass: item.price_type === 'unit' ? String(item.mass) : '',
    bp_product_buying_price: item.unit_price,
    bp_product_selling_price: item.unit_price,
    bp_product_quantity: 0,
    bp_product_size: '',
    bp_minimum_stock_level: 0,
    bp_product_model: '',
    bp_product_image: item.image,
    bp_product_deleted: false,
    bp_product_is_favourite: false,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_variant_id: item.variant_id,
    bp_product_has_variants: Boolean(item.variant_id),
  };
}

function applyCartLineToSnapshot(
  snapshot: DemoProductInformationSnapshot,
  item: DemoCartCheckoutItem,
  variantAttributes?: Record<string, string>,
): DemoProductInformationSnapshot {
  return {
    ...snapshot,
    bp_product_name: item.name || snapshot.bp_product_name,
    bp_product_selling_price: item.unit_price,
    bp_product_image: item.image || snapshot.bp_product_image,
    bp_variant_id: item.variant_id,
    bp_variant_attributes: variantAttributes,
    bp_product_has_variants: Boolean(
      item.variant_id || snapshot.bp_product_has_variants,
    ),
    bp_product_parent_name: item.variant_id
      ? snapshot.bp_product_name
      : undefined,
  };
}

function mapProductToSnapshot(
  product: DemoRow<'demo_products'>,
): DemoProductInformationSnapshot {
  return {
    _id: product.id,
    bp_owner_id: readText(product.bp_owner_id),
    bp_product_location_id: readText(product.bp_product_location_id),
    bp_product_barcode: readText(product.bp_product_barcode),
    bp_product_name: readText(product.bp_product_name),
    bp_product_category: readText(product.bp_product_category),
    bp_product_sub_category: readText(product.bp_product_sub_category),
    bp_product_price_type: normalizePriceType(
      readText(product.bp_product_price_type),
    ),
    bp_product_kind: getProductKind(product),
    bp_product_unit_of_mass: readText(product.bp_product_unit_of_mass),
    bp_product_mass: readText(product.bp_product_mass),
    bp_product_buying_price: readNumber(product.bp_product_buying_price),
    bp_product_selling_price: readNumber(product.bp_product_selling_price),
    bp_product_quantity: readNumber(product.bp_product_quantity),
    bp_product_size: readText(product.bp_product_size),
    bp_minimum_stock_level: readNumber(product.bp_minimum_stock_level),
    bp_product_model: readText(product.bp_product_model),
    bp_product_image: readText(product.bp_product_image),
    bp_product_deleted: readBool(product.bp_product_deleted),
    bp_product_is_favourite: readBool(product.bp_product_is_favourite),
    bp_product_created_at: readNumber(product.bp_product_created_at),
    bp_product_updated_at: readNumber(product.bp_product_updated_at),
    bp_product_has_variants: readBool(product.bp_product_has_variants),
  };
}

function cartLineSnapshotKey(item: DemoCartCheckoutItem): string {
  return `${item.product_id}::${item.variant_id ?? ''}`;
}

async function loadProductSnapshots(
  cartItems: DemoCartCheckoutItem[],
  ownerId: string,
  locationId: string,
  now: number,
): Promise<Map<string, DemoProductInformationSnapshot>> {
  const db = await getDemoDatabase();
  const productMap = new Map<string, DemoProductInformationSnapshot>();

  for (const item of cartItems) {
    const key = cartLineSnapshotKey(item);
    try {
      const product = await getDemoRow(db, 'demo_products', item.product_id);
      const base = mapProductToSnapshot(product);
      const variants = parseJsonArray<ProductVariant>(
        product.bp_product_variants,
      );
      const matched = item.variant_id
        ? variants.find(v => v._id === item.variant_id)
        : null;
      productMap.set(
        key,
        applyCartLineToSnapshot(base, item, matched?.bp_variant_attributes),
      );
    } catch {
      productMap.set(
        key,
        buildFallbackProductSnapshot(item, ownerId, locationId, now),
      );
    }
  }

  return productMap;
}

function toHydratedOrder(
  order: DemoRow<'demo_order_master_information'>,
  payment: DemoPaymentInformationObject,
  sales: DemoSalesProductInformation[],
): DemoHydratedOrder {
  return {
    bp_order_id: order.id,
    bp_order_master_owner_id: readText(order.bp_order_master_owner_id),
    bp_order_master_employee_id: readText(order.bp_order_master_employee_id),
    bp_order_customer_information: readText(
      order.bp_order_customer_information,
    ),
    bp_order_customer_email_information: readText(
      order.bp_order_customer_email_information,
    ),
    bp_order_customer_phone_number_information: readText(
      order.bp_order_customer_phone_number_information,
    ),
    bp_order_customer_information_updated_at: readNumber(
      order.bp_order_customer_information_updated_at,
    ),
    bp_order_master_order_status: readText(
      order.bp_order_master_order_status,
    ) as 'COMPLETED' | 'PENDING' | 'HOLD' | 'PARTIAL',
    bp_order_master_payment_information: payment,
    bp_order_master_receipt_reference_id: readText(
      order.bp_order_master_receipt_reference_id,
    ),
    bp_order_master_receipt_ref_id_updated_at: readNumber(
      order.bp_order_master_receipt_ref_id_updated_at,
    ),
    bp_sales_products_information: sales,
    bp_order_master_created_at: readNumber(order.bp_order_master_created_at),
    bp_order_master_updated_at: readNumber(order.bp_order_master_updated_at),
    bp_order_master_deleted_at: readNumber(order.bp_order_master_deleted_at),
    bp_order_master_location_id: readText(order.bp_order_master_location_id),
    bp_order_master_order_total: readNumber(order.bp_order_master_order_total),
    bp_total_sales_products: sales.length,
  };
}

function mapPaymentRow(
  payment: DemoRow<'demo_payment_information'>,
): DemoPaymentInformationObject {
  return {
    _id: payment.id,
    bp_payment_status: readBool(payment.bp_payment_status),
    bp_payment_amount: readNumber(payment.bp_payment_amount),
    bp_payment_amount_received: readNumber(payment.bp_payment_amount_received),
    bp_payment_already_paid_amount: readNumber(
      payment.bp_payment_already_paid_amount,
    ),
    bp_payment_amount_actually_received: readNumber(
      payment.bp_payment_amount_actually_received,
    ),
    bp_payment_amount_due: readNumber(payment.bp_payment_amount_due),
    bp_payment_amount_refunded: readNumber(payment.bp_payment_amount_refunded),
    bp_payment_refunds: parseJsonArray<DemoPaymentRefundEntry>(
      payment.bp_payment_refunds,
    ),
    bp_order_information_id: readText(payment.bp_order_information_id),
    bp_payment_created_at: readNumber(payment.bp_payment_created_at),
    bp_payment_updated_at: readNumber(payment.bp_payment_updated_at),
    bp_payment_deleted_at: readNumber(payment.bp_payment_deleted_at),
    bp_payments: parseJsonArray<DemoPaymentEntry>(payment.bp_payments),
    bp_payment_location_id: readText(payment.bp_payment_location_id),
    bp_payment_method: readText(payment.bp_payment_method),
  };
}

function mapSaleRow(
  sale: DemoRow<'demo_sales_information'>,
): DemoSalesProductInformation {
  return {
    bp_sales_id: sale.id,
    bp_employee_id: readText(sale.bp_employee_id),
    bp_owner_id: readText(sale.bp_owner_id),
    bp_order_id: readText(sale.bp_order_id),
    bp_product_id: readText(sale.bp_product_id),
    bp_order_quantity: readNumber(sale.bp_order_quantity),
    bp_order_price: readNumber(sale.bp_order_price),
    bp_order_paid_quantity: readNumber(sale.bp_order_paid_quantity),
    bp_order_completed: readBool(sale.bp_order_completed),
    bp_order_partial_paid: readBool(sale.bp_order_partial_paid),
    bp_order_created_at: readNumber(sale.bp_order_created_at),
    bp_order_updated_at: readNumber(sale.bp_order_updated_at),
    bp_order_deleted_at: readNumber(sale.bp_order_deleted_at),
    bp_order_mass: readNumber(sale.bp_order_mass),
    bp_order_paid_mass: readNumber(sale.bp_order_paid_mass),
    bp_sales_location_id: readText(sale.bp_sales_location_id),
    bp_selling_price_changed: readBool(sale.bp_selling_price_changed),
    bp_order_price_type: sale.bp_order_price_type === 'unit' ? 'unit' : 'item',
    bp_product_information: parseJsonObject(
      sale.bp_product_information,
    ) as unknown as DemoProductInformationSnapshot,
  };
}

export function toDemoCheckoutNavigationPayload(
  order: DemoHydratedOrder,
): DemoCheckoutNavigationPayload {
  return {
    order: order.bp_sales_products_information,
    orderID: order.bp_order_id,
    Payment_Information: order.bp_order_master_payment_information,
    ReceiptID: order.bp_order_master_receipt_reference_id ?? '',
  };
}

export async function createDemoOrderInformation(
  params: CreateDemoOrderParams,
): Promise<{
  order: DemoHydratedOrder;
  checkoutPayload: DemoCheckoutNavigationPayload;
}> {
  const now = Date.now();
  const ownerId = params.ownerId || DEFAULT_OWNER_ID;
  const locationId = params.locationId || DEFAULT_LOCATION_ID;
  const employeeId = params.employeeId || DEFAULT_EMPLOYEE_ID;
  const customerName = (params.customerName || 'Walk in customer').trim();
  const customerEmail = normalizeEmail(params.customerEmail || '');
  const customerPhone = (params.customerPhone || '').trim();
  const businessName = (params.businessName || DEFAULT_BUSINESS_NAME).trim();
  const paymentMethod = params.paymentMethod || 'Cash';

  const validItems = params.cartItems
    .map(item => ({
      ...item,
      quantity:
        item.price_type === 'unit' ? 0 : Math.max(0, Math.floor(item.quantity)),
      mass: item.price_type === 'unit' ? Math.max(0, item.mass) : 0,
    }))
    .filter(item =>
      item.price_type === 'unit' ? item.mass > 0 : item.quantity > 0,
    );

  if (validItems.length === 0) {
    throw new Error('No valid cart items to checkout');
  }

  const orderTotal = roundMoney(
    validItems.reduce((sum, item) => sum + toLineTotal(item), 0),
  );
  const receiptReference = await generateUniqueReceiptReferenceId(businessName);
  const productSnapshots = await loadProductSnapshots(
    validItems,
    ownerId,
    locationId,
    now,
  );

  const db = await getDemoDatabase();

  // Payment, order, sales lines and both back-links commit together or not at all.
  const {
    orderId: createdOrderId,
    paymentId: createdPaymentId,
    saleIds: createdSaleIds,
  } = await db.writeTransaction(async tx => {
    const paymentId = await insertDemoRow(tx, 'demo_payment_information', {
      bp_payment_status: toSqlBool(false),
      bp_payment_amount: orderTotal,
      bp_payment_amount_received: 0,
      bp_payment_already_paid_amount: 0,
      bp_payment_amount_actually_received: 0,
      bp_payment_amount_due: orderTotal,
      bp_payment_amount_refunded: 0,
      bp_payment_refunds: JSON.stringify([]),
      bp_order_information_id: '',
      bp_payment_created_at: now,
      bp_payment_updated_at: now,
      bp_payment_deleted_at: 0,
      bp_payments: JSON.stringify([]),
      bp_payment_location_id: locationId,
      bp_payment_method: paymentMethod,
    });

    const orderId = await insertDemoRow(tx, 'demo_order_master_information', {
      bp_order_master_owner_id: ownerId,
      bp_order_master_employee_id: employeeId,
      bp_order_customer_information: customerName,
      bp_order_customer_email_information: customerEmail,
      bp_order_customer_phone_number_information: customerPhone,
      bp_order_customer_information_updated_at: now,
      bp_order_master_order_status: 'PENDING',
      bp_order_master_payment_information: paymentId,
      bp_order_master_receipt_reference_id: receiptReference,
      bp_order_master_receipt_ref_id_updated_at: now,
      bp_sales_products_information: JSON.stringify([]),
      bp_order_master_created_at: now,
      bp_order_master_updated_at: now,
      bp_order_master_deleted_at: 0,
      bp_order_master_location_id: locationId,
      bp_order_master_order_total: orderTotal,
    });

    const saleIds: string[] = [];
    for (const item of validItems) {
      const productInformation =
        productSnapshots.get(cartLineSnapshotKey(item)) ||
        buildFallbackProductSnapshot(item, ownerId, locationId, now);

      const saleId = await insertDemoRow(tx, 'demo_sales_information', {
        bp_employee_id: employeeId,
        bp_owner_id: ownerId,
        bp_order_id: orderId,
        bp_product_id: item.product_id,
        bp_order_quantity: item.quantity,
        bp_order_price: toLineTotal(item),
        bp_order_paid_quantity: 0,
        bp_order_completed: toSqlBool(false),
        bp_order_partial_paid: toSqlBool(false),
        bp_order_created_at: now,
        bp_order_updated_at: now,
        bp_order_deleted_at: 0,
        bp_order_mass: item.mass,
        bp_order_paid_mass: 0,
        bp_sales_location_id: locationId,
        bp_selling_price_changed: toSqlBool(false),
        bp_product_information: JSON.stringify(productInformation),
        bp_order_price_type: item.price_type,
      });
      saleIds.push(saleId);
    }

    await updateDemoRow(tx, 'demo_order_master_information', orderId, {
      bp_sales_products_information: JSON.stringify(saleIds),
      bp_order_master_updated_at: now,
    });

    await updateDemoRow(tx, 'demo_payment_information', paymentId, {
      bp_order_information_id: orderId,
      bp_payment_updated_at: now,
    });

    return { orderId, paymentId, saleIds };
  });

  if (!createdOrderId || !createdPaymentId) {
    throw new Error('Failed to create demo order records');
  }

  const createdOrderRecord = await getDemoRow(
    db,
    'demo_order_master_information',
    createdOrderId,
  );
  const createdPaymentRecord = await getDemoRow(
    db,
    'demo_payment_information',
    createdPaymentId,
  );
  const paymentObject = mapPaymentRow(createdPaymentRecord);
  const createdSalesRecords = await Promise.all(
    createdSaleIds.map(id => getDemoRow(db, 'demo_sales_information', id)),
  );
  const salesObjects = createdSalesRecords.map(mapSaleRow);
  const hydratedOrder = toHydratedOrder(
    createdOrderRecord,
    paymentObject,
    salesObjects,
  );

  return {
    order: hydratedOrder,
    checkoutPayload: toDemoCheckoutNavigationPayload(hydratedOrder),
  };
}

export async function getDemoOrderInformationById(
  orderId: string,
): Promise<DemoHydratedOrder | null> {
  try {
    const db = await getDemoDatabase();
    const orderRecord = await getDemoRow(
      db,
      'demo_order_master_information',
      orderId,
    );
    const salesRecords = await db.getAll<DemoRow<'demo_sales_information'>>(
      'SELECT * FROM demo_sales_information WHERE bp_order_id = ?',
      [orderId],
    );
    const sortedSales = [...salesRecords].sort(
      (a, b) =>
        readNumber(a.bp_order_created_at) - readNumber(b.bp_order_created_at),
    );

    const orderTotal = readNumber(orderRecord.bp_order_master_order_total);
    let paymentObject: DemoPaymentInformationObject = {
      _id: '',
      bp_payment_status: false,
      bp_payment_amount: orderTotal,
      bp_payment_amount_received: 0,
      bp_payment_already_paid_amount: 0,
      bp_payment_amount_actually_received: 0,
      bp_payment_amount_due: orderTotal,
      bp_payment_amount_refunded: 0,
      bp_payment_refunds: [],
      bp_order_information_id: orderRecord.id,
      bp_payment_created_at: readNumber(orderRecord.bp_order_master_created_at),
      bp_payment_updated_at: readNumber(orderRecord.bp_order_master_updated_at),
      bp_payment_deleted_at: 0,
      bp_payments: [],
      bp_payment_location_id: readText(orderRecord.bp_order_master_location_id),
      bp_payment_method: 'Cash',
    };

    const paymentId = readText(orderRecord.bp_order_master_payment_information);
    if (paymentId) {
      try {
        const paymentRecord = await getDemoRow(
          db,
          'demo_payment_information',
          paymentId,
        );
        paymentObject = mapPaymentRow(paymentRecord);
      } catch {
        // Keep fallback payment object when payment record is unavailable.
      }
    }

    const salesObjects = sortedSales.map(mapSaleRow);
    return toHydratedOrder(orderRecord, paymentObject, salesObjects);
  } catch (error) {
    console.error('[getDemoOrderInformationById] Error:', error);
    return null;
  }
}

export async function clearDemoCheckoutSessionData(): Promise<void> {
  const db = await getDemoDatabase();
  await db.writeTransaction(async tx => {
    for (const table of [
      'demo_cart_items',
      'demo_order_master_information',
      'demo_payment_information',
      'demo_sales_information',
    ]) {
      await tx.execute(`DELETE FROM ${table}`);
    }
  });
}
